import type {OcticonComponentName, OcticonName} from '@github-ui/global-navigation-types'
import {isLazyOcticonName, LazyOcticon} from '@github-ui/lazy-octicon'
import {
  AgentIcon,
  AiModelIcon,
  BookIcon,
  CheckCircleIcon,
  ChecklistIcon,
  CodeIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  CreditCardIcon,
  GearIcon,
  GitPullRequestIcon,
  GraphIcon,
  HeartIcon,
  HomeIcon,
  type Icon,
  type IconProps,
  IssueOpenedIcon,
  KeyIcon,
  LawIcon,
  LockIcon,
  MirrorIcon,
  OrganizationIcon,
  PackageIcon,
  PeopleIcon,
  PersonIcon,
  PlayIcon,
  PlugIcon,
  RepoForkedIcon,
  RepoIcon,
  ShieldIcon,
  StarIcon,
  TableIcon,
} from '@primer/octicons-react'
import {type CSSProperties, type ReactNode, useEffect} from 'react'

/**
 * Icons the global navigation renders on virtually every page.
 *
 * Bundling these with the nav is cheaper than code-splitting them: each would
 * otherwise cost a separate chunk request plus a placeholder frame on first
 * paint, for an icon we know is coming. Every other octicon stays on the lazy
 * path, so the nav still avoids pulling in the full icon set.
 */
const eagerOcticons = {
  AgentIcon,
  AiModelIcon,
  BookIcon,
  CheckCircleIcon,
  ChecklistIcon,
  CodeIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  CreditCardIcon,
  GearIcon,
  GitPullRequestIcon,
  GraphIcon,
  HeartIcon,
  HomeIcon,
  IssueOpenedIcon,
  KeyIcon,
  LawIcon,
  LockIcon,
  MirrorIcon,
  OrganizationIcon,
  PackageIcon,
  PeopleIcon,
  PersonIcon,
  PlayIcon,
  PlugIcon,
  RepoForkedIcon,
  RepoIcon,
  ShieldIcon,
  StarIcon,
  TableIcon,
} satisfies Partial<Record<OcticonComponentName, Icon>>

type EagerOcticonComponentName = keyof typeof eagerOcticons

const isEagerOcticonName = (componentName: OcticonComponentName): componentName is EagerOcticonComponentName =>
  componentName in eagerOcticons

function octiconNameToComponentName(iconName: OcticonName): OcticonComponentName {
  return `${iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')}Icon` as OcticonComponentName
}

/**
 * Whether a runtime icon name resolves to an octicon this package can render.
 *
 * Check this before handing `<NavOcticon />` to a slot that wraps its content in
 * chrome of its own — Primer's `leadingVisual`, for example, renders a spacer
 * around whatever element it is given, so an unrecognized name would leave that
 * spacer (and its margin) behind. Such slots should be omitted entirely instead.
 */
export function isNavOcticonName(iconName: string): iconName is OcticonName {
  const componentName = octiconNameToComponentName(iconName as OcticonName)
  return isEagerOcticonName(componentName) || isLazyOcticonName(componentName)
}

const SIZE_KEYWORDS = {small: 16, medium: 32, large: 64} as const

/**
 * Blank box standing in for a lazily-loaded icon, sized to match it and carrying
 * the caller's `className` so any margin or alignment the icon would have had
 * applies while the chunk is in flight. Without it the icon's own styles would
 * only take effect on load, nudging the surrounding content.
 */
function NavOcticonPlaceholder({size = 16, className}: Pick<IconProps, 'size' | 'className'>) {
  const pixels = typeof size === 'number' ? size : SIZE_KEYWORDS[size]
  const style: CSSProperties = {display: 'inline-block', width: pixels, height: pixels, verticalAlign: 'text-bottom'}

  return <span aria-hidden className={className} style={style} />
}

/**
 * Renders `fallback` in place of an octicon whose name we don't recognize, and
 * reports the bad name once the render commits.
 *
 * Throwing from a timeout (rather than calling the deprecated `reportError`)
 * hands the failure to the global handler without breaking the nav. It lives in
 * an effect rather than in render so a payload with a bad name reports once per
 * mount instead of on every re-render, and never from a render React discards.
 */
function UnknownOcticon({name, fallback}: {name: OcticonName; fallback: ReactNode}) {
  useEffect(() => {
    let errorMessage = `Octicon not found: ${name}`

    // use a more detailed error message in non-production environments
    if (process.env.NODE_ENV !== 'production') {
      errorMessage = `Octicon "${name}" is not a recognized icon in @primer/octicons-react. Please ensure a valid octicon name.`
    }

    const timeout = setTimeout(() => {
      throw new Error(errorMessage)
    }, 0)
    return () => clearTimeout(timeout)
  }, [name])

  return fallback ?? null
}

export type NavOcticonProps = {
  /** Kebab-case octicon name from the navigation payload, e.g. `"repo-forked"`. */
  name?: OcticonName
  /**
   * Rendered when `name` is missing or unrecognized, and while a lazily-loaded
   * icon chunk is in flight. Defaults to nothing in the former case and to a
   * blank box sized to `size` in the latter, so the icon does not shift layout.
   */
  fallback?: ReactNode
} & IconProps

/**
 * Renders a navigation octicon named by the server payload.
 *
 * Icons in {@link eagerOcticons} render synchronously from the nav bundle; the
 * rest are code-split and fetched on demand, reserving space while they load.
 * An unrecognized name reports to Failbot and renders `fallback` rather than
 * throwing, so a bad payload never breaks the nav.
 */
export function NavOcticon({name, fallback, ...iconProps}: NavOcticonProps) {
  if (!name) return fallback ?? null

  const componentName = octiconNameToComponentName(name)

  if (isEagerOcticonName(componentName)) {
    const EagerIcon = eagerOcticons[componentName]
    return <EagerIcon {...iconProps} />
  }

  if (!isLazyOcticonName(componentName)) {
    return <UnknownOcticon name={name} fallback={fallback} />
  }

  const placeholder =
    fallback === undefined ? <NavOcticonPlaceholder size={iconProps.size} className={iconProps.className} /> : fallback

  return <LazyOcticon name={componentName} fallback={placeholder} {...iconProps} />
}
